import React, { useRef, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Markdown } from "tiptap-markdown";

import { IconButton, useTheme } from "@mui/material";
import { FormatHeader2Icon } from "@mui-extra/icons/FormatHeader2Icon";
import { FormatHeader1Icon } from "@mui-extra/icons/FormatHeader1Icon";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";

import GradientOverlay from "@/common/GradientOverlay";

/**
 * MarkdownRichEditor — TipTap-based rich-text editor with markdown I/O.
 *
 * Props:
 *  - injectedMarkdown: string — markdown content to render
 *  - onChange: (markdown: string) => void — called on every edit
 *  - onBlur: () => void — called when the editor loses focus
 */
export default function MarkdownRichEditor({
    injectedMarkdown,
    onChange,
    onBlur,
}) {
    const theme = useTheme();
    const scrollRef = useRef(null);
    const [, setTransactionCount] = useState(0);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
            }),
            Underline,
            Table.configure({
                resizable: false,
                HTMLAttributes: { class: "tiptap-table" },
            }),
            TableRow,
            TableCell,
            TableHeader,
            Markdown.configure({
                html: true,
                transformCopiedText: true,
                transformPastedText: true,
            }),
        ],
        content: injectedMarkdown || "",
        onUpdate: ({ editor }) => {
            const md = editor.storage.markdown.getMarkdown();
            onChange && onChange(md);
        },
        onBlur: () => {
            onBlur && onBlur();
        },
        onTransaction: () => {
            setTransactionCount((c) => c + 1);
        },
    });

    // Sync injected markdown when it changes externally (e.g. streaming from LLM)
    useEffect(() => {
        if (!editor) return;
        if (injectedMarkdown === undefined || injectedMarkdown === null) return;

        const currentMarkdown = editor.storage.markdown.getMarkdown();
        if (currentMarkdown !== injectedMarkdown) {
            editor.commands.setContent(injectedMarkdown);
        }
    }, [injectedMarkdown, editor]);

    if (!editor) return null;

    const isActive = (type, attrs) => editor.isActive(type, attrs);

    return (
        <div
            style={{
                position: "relative",
                gap: 12,
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <style>{tiptapStyles(theme)}</style>

            <div style={{ display: "flex", flexWrap: "wrap" }}>
                <ToolbarButton
                    active={isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    icon={<FormatBoldRoundedIcon />}
                />
                <ToolbarButton
                    active={isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    icon={<FormatItalicRoundedIcon />}
                />
                <ToolbarButton
                    active={isActive("underline")}
                    onClick={() =>
                        editor.chain().focus().toggleUnderline().run()
                    }
                    icon={<FormatUnderlinedRoundedIcon />}
                />
                <ToolbarButton
                    active={isActive("heading", { level: 1 })}
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 1 }).run()
                    }
                    icon={<FormatHeader1Icon />}
                />
                <ToolbarButton
                    active={isActive("heading", { level: 2 })}
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                    icon={<FormatHeader2Icon />}
                />
                <ToolbarButton
                    active={isActive("bulletList")}
                    onClick={() =>
                        editor.chain().focus().toggleBulletList().run()
                    }
                    icon={<FormatListBulletedRoundedIcon />}
                />
                <ToolbarButton
                    active={isActive("orderedList")}
                    onClick={() =>
                        editor.chain().focus().toggleOrderedList().run()
                    }
                    icon={<FormatListNumberedRoundedIcon />}
                />
            </div>

            <div
                ref={scrollRef}
                onClick={() => editor.chain().focus().run()}
                style={{
                    overflow: "auto",
                    flexGrow: 1,
                    padding: 12,
                    height: "100%",
                }}
            >
                <EditorContent editor={editor} />
            </div>

            <GradientOverlay scrollRef={scrollRef} />
        </div>
    );
}

function ToolbarButton({ active, onClick, icon }) {
    return (
        <IconButton
            onClick={onClick}
            sx={{
                opacity: active ? 1 : 0.5,
                transition: "opacity 0.15s ease",
            }}
        >
            {icon}
        </IconButton>
    );
}

function tiptapStyles(theme) {
    const borderColor = theme.palette.divider;
    const cellBg = theme.palette.background.paper;
    const headerBg = theme.palette.action.hover;

    return `
        .tiptap {
            outline: none;
            font-size: 14px;
            line-height: 1.6;
        }
        .tiptap p {
            margin: 0.4em 0;
        }
        .tiptap h1, .tiptap h2, .tiptap h3 {
            margin: 0.6em 0 0.3em;
        }
        .tiptap ul, .tiptap ol {
            padding-left: 1.4em;
        }
        .tiptap table,
        .tiptap-table {
            border-collapse: collapse;
            width: 100%;
            margin: 0.8em 0;
        }
        .tiptap table td,
        .tiptap table th,
        .tiptap-table td,
        .tiptap-table th {
            border: 1px solid ${borderColor};
            padding: 6px 10px;
            min-width: 60px;
            vertical-align: top;
        }
        .tiptap table th,
        .tiptap-table th {
            font-weight: 600;
        }
        .tiptap-table .selectedCell {
            background: ${theme.palette.action.selected};
        }
    `;
}
